import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import jwtDecode from 'jwt-decode';
import {
    Subject,
    filter,
    firstValueFrom,
    forkJoin,
    startWith,
    take,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/auth.service';
import { LoginService } from '../../login/login.service';
// import { CustomerAlertDialogComponent } from '../components/customer-alert-dialog/customer-alert-dialog.component';
import { DOCUMENT } from '@angular/common';
import { AuthRoles } from '../../shared/enums/auth-roles.enum';
import {
    ACCESS_KEYS,
    LocalStorageService,
} from '../../shared/services/local-storage.service';
import { AppService } from '../../app.service';
import { EntitlementService } from '../../entitlements.service';
import { EmailDomainDialogComponent } from '../../components/email-domain-dialog/email-domain-dialog.component';
import { MatDialog } from '@angular/material/dialog';

type NavigationItem = {
    routerLink?: string | any;
    svgIcon?: string | any;
    matIcon?: string | any;
    title?: string;
    requiredRoles: AuthRoles[];
    children?: NavigationItem[];
    viewExpanded?: boolean;
    isDisabled?: boolean;
};

export type AssetIcon = {
    name: string;
    path: string;
};

@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
})
export class NavigationComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    isNavigationMenuOpen = false;
    navigationMenuWidth = 56;
    sidenavContentLeftMargin = 60;
    pressroomPath = '';

    userName: string = '';
    fullName: string = '';
    emailId: string = '';
    currentRoute: any = '';

    accountMenu: any;
    accountName = 'Select Account';
    isMenuOpen: boolean | undefined;
    contentMargin = 56;
    pressroomId: any;
    navDest: any;
    iconList: HTMLElement | undefined;
    currentAccount: string;
    emailListener: string | undefined;
    isLoading: Boolean = false;
    isNewUser: Boolean = false;
    currentYear = new Date().getFullYear();

    entitlements = {
        enableMediaConnect: false,
        enableOutreachMediaConnect: false,
        enableContactsMediaConnect: false,
        enableGlobalSearch: false,
        enableMediaMonitoring: false,
        enableAnalyticsContentManager: false,
        enableSubscriberContentManager: false,
        enableDataMigrationContentManager: false,
        enableApprovedSenderOutreach: false,
        enablePressroom: true,
    };

    allNavigationItems: NavigationItem[] = [
        {
            routerLink: '/dashboard/content-manager',
            svgIcon: 'content-manager',
            title: 'Content Manager',
            requiredRoles: [],
            isDisabled: false,
            viewExpanded: false,
        },
        {
            routerLink: '/dashboard/media-assets',
            svgIcon: 'bpGallary',
            title: 'Media Assets',
            requiredRoles: [
                AuthRoles.MODERATOR,
                AuthRoles.ADMIN,
                AuthRoles.SUPERADMIN,
            ],
            isDisabled: false,
            viewExpanded: false,
        },
        {
            routerLink: '/dashboard/website-config',
            svgIcon: 'web-config',
            title: 'Pressroom Design',
            requiredRoles: [
                AuthRoles.MODERATOR,
                AuthRoles.ADMIN,
                AuthRoles.SUPERADMIN,
            ],
            isDisabled: false,
            viewExpanded: false,
        },
        {
            routerLink: '/media-connect',
            matIcon: 'campaign',
            title: 'Media Connect',
            requiredRoles: [],
            viewExpanded: true,
            isDisabled: false,
            children: [
                {
                    routerLink: '/media-connect/global-search',
                    title: 'Global Search',
                    isDisabled: false,
                    requiredRoles: [],
                },
                {
                    routerLink: '/media-connect/contacts',
                    title: 'Media Contacts',
                    isDisabled: false,
                    requiredRoles: [],
                },

                {
                    routerLink: '/media-connect/outreach',
                    title: 'Outreach',
                    isDisabled: false,
                    requiredRoles: [],
                },
            ],
        },
        {
            routerLink: '/media-monitoring',
            matIcon: 'travel_explore',
            title: 'Media Monitoring',
            requiredRoles: [],
            isDisabled: false,
            viewExpanded: false,
        },
        {
            routerLink: '/settings',
            svgIcon: 'settings',
            title: 'Settings',
            requiredRoles: [AuthRoles.ADMIN, AuthRoles.SUPERADMIN],
            viewExpanded: true,
            isDisabled: false,
            children: [
                {
                    routerLink: '/settings/admin',
                    title: 'Account Admin',
                    isDisabled: false,
                    requiredRoles: [AuthRoles.ADMIN, AuthRoles.SUPERADMIN],
                },
                {
                    routerLink: '/settings/domain-settings',
                    title: 'Sender Domain',
                    isDisabled: false,
                    requiredRoles: [AuthRoles.ADMIN, AuthRoles.SUPERADMIN],
                },
                {
                    routerLink: '/settings/super-admin',
                    title: 'Super Admin',
                    isDisabled: false,
                    requiredRoles: [AuthRoles.SUPERADMIN],
                },
            ],
        },
    ];
    filteredNavigationItems: NavigationItem[] = [];

    icons: AssetIcon[] = [
        { name: 'avatar', path: 'assets/icons/avatar.svg' },
        { name: 'close', path: 'assets/icons/close.svg' },
        { name: 'dashboard', path: 'assets/icons/dashboard.svg' },
        { name: 'home', path: 'assets/icons/home.svg' },
        { name: 'blumepress', path: 'assets/icons/blumepress.svg' },
        { name: 'logo-v', path: 'assets/icons/logo-v.svg' },
        { name: 'menu', path: 'assets/icons/menu.svg' },
        { name: 'settings', path: 'assets/icons/settings.svg' },
        { name: 'content-manager', path: 'assets/icons/content-manager.svg' },
        { name: 'web-config', path: 'assets/icons/web-config.svg' },
        { name: 'outreach', path: 'assets/icons/outreach.svg' },
        { name: 'noContent', path: 'assets/icons/noContent.svg' },

        //  Webconfig
        { name: 'bpTheme', path: 'assets/webConf/theme.svg' },
        { name: 'bpHeader', path: 'assets/webConf/header.svg' },
        { name: 'bpArticle', path: 'assets/webConf/article.svg' },
        { name: 'bpBody', path: 'assets/webConf/body.svg' },
        { name: 'bpFooter', path: 'assets/webConf/footer.svg' },
        { name: 'bpGallary', path: 'assets/webConf/gallary.svg' },
        { name: 'bpEmbed', path: 'assets/webConf/embed.svg' },
    ];
    currentUserRoles!: AuthRoles[];
    moderatorAdminRoles = [
        AuthRoles.MODERATOR,
        AuthRoles.ADMIN,
        AuthRoles.SUPERADMIN,
    ];
    isAdminOrSuperAdminOrModerator = false;

    constructor(
        private iconRegistry: MatIconRegistry,
        private sanitizer: DomSanitizer,
        private router: Router,
        private auth: AuthService,
        public route: Router,
        public loginService: LoginService,
        private http: HttpClient,
        private localStorageService: LocalStorageService,
        private appService: AppService,
        private entitlementService: EntitlementService,
        public dialog: MatDialog,
        private authService: AuthService,
        @Inject(DOCUMENT) private document: Document,
    ) {
        this.icons.forEach((icon) => this.setupIcon(icon));
        const details: any = jwtDecode(
            this.localStorageService.getItem(ACCESS_KEYS.AUTH_TOKEN) || '',
        );
        const roles = details?.user?.roles ?? [];
        this.isAdminOrSuperAdminOrModerator = roles.some((role: any) =>
            this.moderatorAdminRoles.includes(role),
        );

        this.userName = `${details.user.firstName[0]}${details.user.lastName[0]}`;
        this.fullName = `${details.user.firstName} ${details.user.lastName}`;
        this.emailId = details.user.email;
        this.currentUserRoles = details.user.roles || [];
        this.currentAccount =
            this.localStorageService.getItem(ACCESS_KEYS.CURRENT_ACCOUNT) || '';
        this.pressroomId =
            this.localStorageService.getItem(ACCESS_KEYS.CURRENT_PRESSROOM) ||
            '';
        this.pressroomPath =
            this.localStorageService.getItem(
                ACCESS_KEYS.CURRENT_PRESSROOM_PATH,
            ) || '';
        this.navDest = this.router.parseUrl(
            this.router.url,
        ).root.children.primary?.segments?.[1]?.path;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit() {
        this.closeNavigationMenu();

        this.loginService.updateAccountList().then((value: any) => {
            this.accountMenu = value;
            const curruentAccData: { id: string; name: any }[] = [];
            value.forEach((element: { id: string; name: any }) => {
                if (this.currentAccount === element.id) {
                    this.accountName = element.name;
                    curruentAccData.push(element);
                }
            });
            this.setCurrentPressroom(curruentAccData);
            this.emailListener = `${environment.email}+${this.pressroomId}${environment.emailDomain}`;
        });

        const fetchUserInfoPromise = this.loginService.fetchUserInfo(
            this.pressroomId !== 'noPressroom',
            this.pressroomId || '',
            this.currentAccount || '',
            this.pressroomPath || '',
        );

        fetchUserInfoPromise.then((result) => {
            this.runConfig(result);
            this.filteredNavigationItems = this.filterNavigationItemsForUser(
                this.allNavigationItems,
            );
        });
    }

    private setupIcon(icon: AssetIcon) {
        this.iconRegistry.addSvgIcon(
            icon.name,
            this.sanitizer.bypassSecurityTrustResourceUrl(icon.path),
        );
    }

    private filterNavigationItemsForUser(
        navigationItems: NavigationItem[],
    ): NavigationItem[] {
        return navigationItems
            .filter((item) => {
                const hasRoleAccess = item.requiredRoles?.length
                    ? item.requiredRoles.some((role) =>
                          this.currentUserRoles.includes(role),
                      )
                    : true;

                if (!hasRoleAccess) return false;

                // Entitlement-based filtering
                if (
                    item.routerLink === '/settings/domain-settings' &&
                    !this.entitlements.enableApprovedSenderOutreach
                ) {
                    return false;
                }

                if (
                    item.routerLink === '/media-connect' &&
                    !this.entitlements.enableMediaConnect
                ) {
                    return false;
                }

                if (
                    item.routerLink === '/media-monitoring' &&
                    !this.entitlements.enableMediaMonitoring
                ) {
                    return false;
                }

                // Hide pressroom-related tabs when pressroom is disabled
                if (!this.entitlements.enablePressroom) {
                    if (
                        item.routerLink === '/dashboard/content-manager' ||
                        item.routerLink === '/dashboard/media-assets' ||
                        item.routerLink === '/dashboard/website-config'
                    ) {
                        return false;
                    }
                }

                return true;
            })
            .map((item) => {
                if (item.children && item.children.length > 0) {
                    item.children = this.filterNavigationItemsForUser(
                        item.children,
                    ).filter((child) => {
                        if (
                            child.routerLink === '/media-connect/outreach' &&
                            !this.entitlements.enableOutreachMediaConnect
                        ) {
                            return false;
                        }
                        if (
                            child.routerLink === '/media-connect/contacts' &&
                            !this.entitlements.enableContactsMediaConnect
                        ) {
                            return false;
                        }
                        if (
                            child.routerLink ===
                                '/media-connect/global-search' &&
                            !this.entitlements.enableGlobalSearch
                        ) {
                            return false;
                        }
                        return true;
                    });
                }
                return item;
            });
    }

    changePassword() {
        this.route.navigateByUrl('/settings/user-account');
    }

    copyEmail(email: any) {
        navigator.clipboard
            .writeText(email)
            .then(() => {
                console.log('Email copied to clipboard:', email);
            })
            .catch((err) => {
                console.error('Could not copy email: ', err);
            });
    }

    openEmailConfig() {
        const dialogRef = this.dialog.open(EmailDomainDialogComponent, {
            width: '600px',
            maxHeight: '100vh',
            data: { pressroomId: this.pressroomId },
        });
    }

    goToLiveSite() {
        const segment = this.localStorageService.getLivePressroomSegment();
        if (!segment) {
            return;
        }
        window.open(environment.livesiteUrl + segment + '/home');
    }

    runConfig(accountInfo: any) {
        this.entitlements = {
            enableMediaConnect: accountInfo.enableMediaConnect,
            enableOutreachMediaConnect: accountInfo.enableOutreachMediaConnect,
            enableContactsMediaConnect: accountInfo.enableContactsMediaConnect,
            enableGlobalSearch: accountInfo.enableGlobalSearch,
            enableMediaMonitoring: accountInfo.enableMediaMonitoring,
            enableAnalyticsContentManager:
                accountInfo.enableAnalyticsContentManager,
            enableSubscriberContentManager:
                accountInfo.enableSubscriberContentManager,
            enableDataMigrationContentManager:
                accountInfo.enableDataMigrationContentManager,
            enableApprovedSenderOutreach:
                accountInfo.enableApprovedSenderOutreach,
            enablePressroom: accountInfo.enablePressroom !== false,
        };

        this.entitlementService.setEntitlements(this.entitlements || {});
        this.filteredNavigationItems = this.filterNavigationItemsForUser(
            this.allNavigationItems,
        );

        this.route.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd,
                ),
                startWith(this.route),
            )
            .subscribe((event: any) => {
                const url = event.urlAfterRedirects || event.url;
                if (!url.includes('/login')) {
                    this.currentRoute = this.getTitleFromUrl(url);
                }

                // Check access here
                if (!this.canAccessRoute(url)) {
                    this.route.navigateByUrl(this.getDefaultRoute());
                }
            });

        this.isLoading = false;
    }

    private getDefaultRoute(): string {
        if (!this.entitlements.enablePressroom) {
            if (this.entitlements.enableMediaConnect) {
                return '/media-connect';
            }
            if (this.entitlements.enableMediaMonitoring) {
                return '/media-monitoring';
            }
            return '/settings/admin';
        }
        return '/dashboard/content-manager';
    }

    private canAccessRoute(url: string): boolean {
        if (
            url.startsWith('/settings/domain-settings') &&
            !this.entitlements.enableApprovedSenderOutreach
        ) {
            return false;
        }

        if (url.startsWith('/media-connect')) {
            if (!this.entitlements.enableMediaConnect) return false;

            if (
                url.includes('/outreach') &&
                !this.entitlements.enableOutreachMediaConnect
            )
                return false;
            if (
                url.includes('/contacts') &&
                !this.entitlements.enableContactsMediaConnect
            )
                return false;
        }

        if (
            url.startsWith('/media-monitoring') &&
            !this.entitlements.enableMediaMonitoring
        ) {
            return false;
        }

        // Block pressroom-related routes when pressroom is disabled
        if (!this.entitlements.enablePressroom) {
            if (
                url.startsWith('/dashboard/content-manager') ||
                url.startsWith('/dashboard/add-edit-release') ||
                url.startsWith('/dashboard/media-assets') ||
                url.startsWith('/dashboard/website-config')
            ) {
                return false;
            }
        }

        return true;
    }

    getTitleFromUrl(url: string) {
        if (url.includes('add-edit-release')) {
            return 'Release Configuration';
        }

        if (url.includes('add-edit-outreach')) {
            return 'Outreach Configuration';
        }
        for (const navItem of this.filteredNavigationItems) {
            // First check if this parent has matching children
            if (navItem.children && navItem.children.length) {
                const childMatch = navItem.children.find((child) =>
                    url.includes(child.routerLink),
                );
                if (childMatch) {
                    return childMatch.title;
                }
            }

            // If no child matched, check the parent itself
            if (url.includes(navItem.routerLink)) {
                return navItem.title;
            }
        }

        // Fallback if nothing matched
        return 'Pressroom';
    }

    toggleNav() {
        this.isMenuOpen = !this.isMenuOpen;
        if (this.isMenuOpen) {
            this.contentMargin = 265;
        } else {
            this.contentMargin = 56;
        }
    }

    openNavigationMenu() {
        this.isNavigationMenuOpen = true;
        this.navigationMenuWidth = 265;
    }

    closeNavigationMenu() {
        this.isNavigationMenuOpen = false;
        this.navigationMenuWidth = 56;
    }

    logout() {
        this.loginService.logout();
    }

    navigate(dest: any) {
        this.route.navigateByUrl(`${dest}`);
    }

    setCurrentPressroom(value: string | any[]) {
        if (this.pressroomId?.toString() === 'noPressroom') {
            if (value.length > 0) {
                if (value[0].pressrooms.length > 0) {
                    this.pressroomId = value[0].pressrooms[0].id;
                    this.localStorageService.setItemSafe(
                        ACCESS_KEYS.CURRENT_PRESSROOM,
                        value[0].pressrooms[0].id,
                    );
                    this.localStorageService.setItemSafe(
                        ACCESS_KEYS.CURRENT_PRESSROOM_PATH,
                        value[0].pressrooms[0].path,
                    );
                } else {
                    this.localStorageService.setItem(
                        ACCESS_KEYS.CURRENT_PRESSROOM,
                        'noPressroom',
                    );
                }
            } else {
                this.localStorageService.setItem(
                    ACCESS_KEYS.CURRENT_PRESSROOM,
                    'noPressroom',
                );
            }
        }
    }

    async updatePressroom(accountId: string, pressroomId: string) {
        const currentPressroomId = this.pressroomId;
        const currentAccountId = this.currentAccount;

        if (
            pressroomId === currentPressroomId &&
            accountId === currentAccountId
        ) {
            return;
        }

        // Set flags IMMEDIATELY — before the async HTTP call — so that:
        //  1. ALL beforeunload handlers return early (Chrome silently
        //     cancels reload if any handler calls preventDefault without
        //     a user gesture, and the gesture expires during the HTTP
        //     round-trip).
        //  2. getPreviewUrl() and any other CD-driven readers freeze on
        //     the current state instead of half-updating with the new
        //     localStorage values before the page actually reloads.
        (window as any).__pp_skipUnloadGuard = true;
        (window as any).__pp_switchInProgress = true;

        try {
            const updated = await this.getUpdatedToken(accountId, pressroomId);
            if (updated) {
                window.location.reload();
                // Safety net: if reload was silently blocked (edge-case
                // browser behavior), force a full navigation after 300ms.
                setTimeout(() => {
                    window.location.href =
                        window.location.origin + window.location.pathname;
                }, 300);
            } else {
                (window as any).__pp_skipUnloadGuard = false;
                (window as any).__pp_switchInProgress = false;
            }
        } catch (err) {
            (window as any).__pp_skipUnloadGuard = false;
            (window as any).__pp_switchInProgress = false;
            console.error('Failed to update token or pressroom info', err);
        }
    }

    // POST resetToken
    async getUpdatedToken(
        accountId: string,
        pressroomId: string,
    ): Promise<boolean> {
        const body = { accountId, pressroomId };
        try {
            const result: any = await firstValueFrom(
                this.http.post(
                    `${environment.url}api/v1/identity/resetToken`,
                    body,
                ),
            );
            this.authService.setTokenMetadata(result.token);
            return true;
        } catch (error) {
            console.error('Something went wrong with new token!', error);
            throw error;
        }
    }
}
